import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import {
  acceptDeclaration, capabilityRefusal, DECLARATION_POINTS, DECLARATION_VERSION, DECLARED_LICENSE_LABEL,
  distributorStage, effectivePinSaleDocument, getCapability, setDistributorRole, setPinSaleDocument,
  type DeclaredLicense, type OperatorCapability, type PinSaleDocument,
} from "../../api/capability";

const holdsAny = (roles: string[], allowed: readonly string[]) =>
  roles.some((r) => allowed.includes(r.toLowerCase()));

const when = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) + " IST";

/**
 * Internet Distributor (owner 2026-10-26): a top-level business that buys
 * bandwidth upstream, mints its own pins and sells them, usually without a
 * licence, giving vouchers and an own-name tax invoice only when asked.
 *
 * Three people, three steps, kept apart on purpose:
 *   1. superadmin switches the role on (radaccess refuses a sub-operator);
 *   2. the operator's OWNER accepts the declaration -- nobody else may, not
 *      Admin and not superadmin, because it is their responsibility;
 *   3. Owner or Admin picks the default pin-sale document.
 * Until step 2 the role is only a label: nothing it unlocks applies.
 *
 * 🔑 Hidden from everyone but superadmin while the namespace is not a
 * distributor: an ordinary operator has nothing to do here.
 */
export function DistributorCard({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const superadmin = holdsAny(identity.roles, ["superadmin"]);
  const owner = holdsAny(identity.roles, ["operator"]);
  const mayChooseDocument = holdsAny(identity.roles, ["superadmin", "operator", "manager"]);

  const load = useQuery({
    queryKey: ["capability", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async () => (await getCapability(identity, ns)).data,
  });
  const store = (c: OperatorCapability) => qc.setQueryData(["capability", ns], c);

  const role = useMutation({ mutationFn: async (on: boolean) => (await setDistributorRole(identity, ns, on)).data, onSuccess: store });
  const [declared, setDeclared] = useState<DeclaredLicense | "">("");
  const [agreed, setAgreed] = useState(false);
  const accept = useMutation({
    mutationFn: async () => (await acceptDeclaration(identity, ns, declared as DeclaredLicense)).data,
    onSuccess: store,
  });
  const doc = useMutation({ mutationFn: async (d: PinSaleDocument) => (await setPinSaleDocument(identity, ns, d)).data, onSuccess: store });

  const c = load.data;
  const stage = distributorStage(c);
  if (!superadmin && (load.isError || stage === "none")) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Internet Distributor</Typography>
        {stage === "active" && <Chip size="small" color="success" label="Active" />}
        {stage === "awaiting-declaration" && <Chip size="small" color="warning" label="Waiting for the Owner's declaration" />}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A distributor buys bandwidth from an upstream provider, makes its own pins and sells them to other operators. It
        gives a voucher for each sale, or a tax invoice in its own name when the buyer asks. A licensed VNO or ISP always
        gives a tax invoice for internet sales.
      </Typography>

      {load.isLoading && <Typography variant="body2">Loading…</Typography>}
      {load.isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Could not load distributor settings</AlertTitle>
          {capabilityRefusal(load.error)}
        </Alert>
      )}

      {c && superadmin && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ flex: 1 }}>
            {stage === "none"
              ? <>Make <b>{ns}</b> an Internet Distributor. Its Owner then accepts the declaration before it can publish pins.</>
              : <>Remove the Internet Distributor role from <b>{ns}</b>. Its pin plans stop being publishable unless it is an ISP or VNO.</>}
          </Typography>
          <Button
            variant={stage === "none" ? "contained" : "outlined"}
            color={stage === "none" ? "primary" : "warning"}
            disabled={role.isPending}
            onClick={() => { role.reset(); role.mutate(stage === "none"); }}
          >
            {role.isPending ? "Saving…" : stage === "none" ? "Make distributor" : "Remove role"}
          </Button>
        </Stack>
      )}
      {role.error && <Alert severity="error" sx={{ mb: 2 }}><AlertTitle>Not changed</AlertTitle>{capabilityRefusal(role.error)}</Alert>}

      {c && stage === "awaiting-declaration" && (
        owner ? (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Owner's declaration</Typography>
            <Alert severity="info" sx={{ mb: 1 }}>Draft wording ({DECLARATION_VERSION}). The final text may change; you will be asked again if it does.</Alert>
            <ul style={{ marginTop: 0 }}>
              {DECLARATION_POINTS.map((p) => <li key={p}><Typography variant="body2">{p}</Typography></li>)}
            </ul>
            <RadioGroup value={declared} onChange={(e) => { accept.reset(); setDeclared(e.target.value as DeclaredLicense); }}>
              {(Object.keys(DECLARED_LICENSE_LABEL) as DeclaredLicense[]).map((k) => (
                <FormControlLabel key={k} value={k} control={<Radio size="small" />} label={DECLARED_LICENSE_LABEL[k]} />
              ))}
            </RadioGroup>
            <FormControlLabel
              control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
              label="I have read the points above and accept them as the Owner of this business."
            />
            {accept.error && <Alert severity="error" sx={{ my: 1 }}><AlertTitle>Not accepted</AlertTitle>{capabilityRefusal(accept.error)}</Alert>}
            <Button variant="contained" disabled={!declared || !agreed || accept.isPending} onClick={() => accept.mutate()}>
              {accept.isPending ? "Saving…" : "Accept declaration"}
            </Button>
          </Paper>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Only the Owner of {ns} can accept the declaration. Until they do, this provider cannot publish pins as a distributor.
          </Alert>
        )
      )}

      {c && stage === "active" && (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Declaration accepted{c.responsibilityAcceptedAt && <> {when(c.responsibilityAcceptedAt)}</>}
            {c.declaredLicense && <> · {DECLARED_LICENSE_LABEL[c.declaredLicense as DeclaredLicense] ?? c.declaredLicense}</>}
            {c.responsibilityTextVersion && <> · wording {c.responsibilityTextVersion}</>}
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>For a pin sale, give by default</Typography>
          <ToggleButtonGroup
            size="small" exclusive
            value={effectivePinSaleDocument(c)}
            disabled={!mayChooseDocument || doc.isPending}
            onChange={(_, v: PinSaleDocument | null) => { if (v && v !== effectivePinSaleDocument(c)) { doc.reset(); doc.mutate(v); } }}
          >
            <ToggleButton value="voucher">Voucher</ToggleButton>
            <ToggleButton value="tax_invoice">Tax invoice in my name</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            A tax invoice uses your own name, GSTIN and invoice series. If you hold a verified VNO or ISP licence, internet
            sales are always invoiced whatever you choose here.
          </Typography>
          {doc.error && <Alert severity="error" sx={{ mt: 1 }}><AlertTitle>Not saved</AlertTitle>{capabilityRefusal(doc.error)}</Alert>}
          {doc.isSuccess && <Alert severity="success" sx={{ mt: 1 }}>Saved.</Alert>}
        </>
      )}
    </Paper>
  );
}
